import {
  JournalInfoTable,
  type InternalTableProps,
} from '@/components/InvoiceReportDetailDrawer/JournalInfo';
import { ErrorBoundary } from '@/components/Rollbar';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useTranslation } from '@/i18n';
import { FC, memo, Suspense, useMemo } from 'react';

import {
  useDefaultPagination,
  useJournalEntries,
} from '../hooks/useJournalEntries';

type InnerListProps = { reportId: string };

type ErrorJournalInfoTableProps = {
  error: Error | null;
};

export const ErrorJournalInfoTable: FC<ErrorJournalInfoTableProps> = memo(
  () => {
    const pagination = useDefaultPagination();
    const tableProps = useMemo(
      () => ({ data: [], loading: false, emptyType: 'error' as const }),
      []
    );
    return <JournalInfoTable table={tableProps} pagination={pagination} />;
  }
);
ErrorJournalInfoTable.displayName = 'ErrorJournalInfoTable';

export const LoadingJournalInfoTable: FC = memo(() => {
  const isOnline = useNetworkStatus();
  const pagination = useDefaultPagination();
  const tableProps = useMemo(
    () => ({ data: [], loading: isOnline }),
    [isOnline]
  );
  return <JournalInfoTable table={tableProps} pagination={pagination} />;
});
LoadingJournalInfoTable.displayName = 'LoadingJournalInfoTable';

const InnerList: FC<InnerListProps> = memo(({ reportId }) => {
  const { data, listColumns } = useJournalEntries(reportId);
  const { t } = useTranslation();
  const pagination = useDefaultPagination();
  const table: InternalTableProps = useMemo(
    () => ({
      data: data ?? [],
      emptyText: <>{t('journal_info_empty_message')}</>,
    }),
    [data, t]
  );
  return (
    <JournalInfoTable
      table={table}
      pagination={pagination}
      listColumns={listColumns}
    />
  );
});
InnerList.displayName = 'InnerList';

export type JournalInfoTableViewProps = { reportId: string };

export const JournalInfoTableView: FC<JournalInfoTableViewProps> = memo(
  ({ reportId }) => {
    return (
      <ErrorBoundary fallback={ErrorJournalInfoTable}>
        <Suspense fallback={<LoadingJournalInfoTable />}>
          <InnerList reportId={reportId} />
        </Suspense>
      </ErrorBoundary>
    );
  }
);
JournalInfoTableView.displayName = 'JournalInfoTableView';

export type { InternalTableProps };
